/* =====================================================================
 * CAD Material (자재관리) API client — `/api/v1/cad/material/*` 의 thin wrapper.
 *
 * 자재관리 화면(엑셀 분석 + 멀티턴 채팅)이 사용하는 엔드포인트만 노출한다.
 * CAD 메인 라인(`/api/v1/cad/*`) 과 동일 도메인 안에서 prefix 만 분리.
 *
 * 멀티테넌트
 *   본 클라이언트는 CAD 가 활성화된 테넌트(B2C local / dev / prod) 에서만
 *   호출된다. Samsung onprem 매니페스트엔 CAD 가 없으므로 자재관리 화면
 *   진입 자체가 없다.
 * ===================================================================== */

import type { WorkspaceFile } from '@auw/workspace-utils';
import { authFetch, getHeaders as baseGetHeaders, API_BASE_URL } from './authFetch';

const MATERIAL_PREFIX = `${API_BASE_URL}/api/v1/cad/material`;

function jsonHeaders(): Record<string, string> {
  return baseGetHeaders(true);
}

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface CadMaterialProject {
  project_id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CadMaterialFolder {
  folder_id: string;
  project_id: string;
  parent_folder_id: string | null;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CadMaterialUploadResponse {
  uploaded: WorkspaceFile[];
  count: number;
  errors: Array<{ file: string; error: string }> | null;
}

export interface CadMaterialMessage {
  message_id: string;
  project_id: string;
  /** file-scoped session 도입 후 추가. NULL 은 legacy/프로젝트 전반 메시지. */
  file_id?: string | null;
  role: 'user' | 'assistant' | 'system';
  content: string;
  attachments: unknown[];
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface CadMaterialMessagesResponse {
  messages: CadMaterialMessage[];
  count: number;
}

export type CadMaterialCellValue = string | number | boolean | null;

export interface CadMaterialSheet {
  name: string;
  rows: CadMaterialCellValue[][];
  row_count: number;
  col_count: number;
  truncated_rows: boolean;
  truncated_cols: boolean;
}

export interface CadMaterialPreviewLimits {
  max_rows: number;
  max_cols: number;
}

export interface CadMaterialPreviewData {
  sheets: CadMaterialSheet[];
  display_name?: string | null;
  file_id?: string | null;
  preview_limits: CadMaterialPreviewLimits;
}

export interface CadMaterialPreviewResponse {
  status: 'completed' | 'failed';
  data: CadMaterialPreviewData | null;
  error_message?: string | null;
}

/* ------------------------------------------------------------------ */
/* Projects                                                            */
/* ------------------------------------------------------------------ */

export async function listCadMaterialProjects(): Promise<CadMaterialProject[]> {
  const res = await authFetch(`${MATERIAL_PREFIX}/projects`, { headers: jsonHeaders() });
  const data = await unwrap<{ projects: CadMaterialProject[]; count: number }>(res);
  return data.projects ?? [];
}

export async function createCadMaterialProject(name: string): Promise<CadMaterialProject> {
  const res = await authFetch(`${MATERIAL_PREFIX}/projects`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  const data = await unwrap<{ project: CadMaterialProject }>(res);
  return data.project;
}

export async function renameCadMaterialProject(
  projectId: string,
  name: string,
): Promise<CadMaterialProject> {
  const res = await authFetch(`${MATERIAL_PREFIX}/projects/${encodeURIComponent(projectId)}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  const data = await unwrap<{ project: CadMaterialProject }>(res);
  return data.project;
}

export async function deleteCadMaterialProject(projectId: string): Promise<void> {
  const res = await authFetch(`${MATERIAL_PREFIX}/projects/${encodeURIComponent(projectId)}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  await unwrap<{ deleted: boolean }>(res);
}

/* ------------------------------------------------------------------ */
/* Folders                                                             */
/* ------------------------------------------------------------------ */

export async function listCadMaterialFolders(projectId: string): Promise<CadMaterialFolder[]> {
  const res = await authFetch(
    `${MATERIAL_PREFIX}/projects/${encodeURIComponent(projectId)}/folders`,
    { headers: jsonHeaders() },
  );
  const data = await unwrap<{ folders: CadMaterialFolder[]; count: number }>(res);
  return data.folders ?? [];
}

export async function createCadMaterialFolder(
  projectId: string,
  name: string,
  parentFolderId?: string | null,
): Promise<CadMaterialFolder> {
  const res = await authFetch(
    `${MATERIAL_PREFIX}/projects/${encodeURIComponent(projectId)}/folders`,
    {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ name, parent_folder_id: parentFolderId ?? null }),
    },
  );
  const data = await unwrap<{ folder: CadMaterialFolder }>(res);
  return data.folder;
}

export async function renameCadMaterialFolder(
  folderId: string,
  name: string,
): Promise<CadMaterialFolder> {
  const res = await authFetch(`${MATERIAL_PREFIX}/folders/${encodeURIComponent(folderId)}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  const data = await unwrap<{ folder: CadMaterialFolder }>(res);
  return data.folder;
}

export async function deleteCadMaterialFolder(folderId: string): Promise<void> {
  const res = await authFetch(`${MATERIAL_PREFIX}/folders/${encodeURIComponent(folderId)}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  await unwrap<{ deleted: boolean }>(res);
}

/* ------------------------------------------------------------------ */
/* Files                                                               */
/* ------------------------------------------------------------------ */

export interface CadMaterialFileListOptions {
  folderId?: string | null;
  includeAllFolders?: boolean;
}

export async function listCadMaterialFiles(
  projectId: string,
  options: CadMaterialFileListOptions = {},
): Promise<WorkspaceFile[]> {
  const params = new URLSearchParams();
  if (options.folderId) params.set('folder_id', options.folderId);
  if (options.includeAllFolders) params.set('include_all_folders', 'true');
  const qs = params.toString();
  const url =
    `${MATERIAL_PREFIX}/projects/${encodeURIComponent(projectId)}/files`
    + (qs ? `?${qs}` : '');
  const res = await authFetch(url, { headers: jsonHeaders() });
  const data = await unwrap<{ files: WorkspaceFile[]; count: number }>(res);
  return data.files ?? [];
}

export async function uploadCadMaterialFiles(
  projectId: string,
  files: File[],
  folderId?: string | null,
): Promise<CadMaterialUploadResponse> {
  const form = new FormData();
  for (const f of files) {
    form.append('files', f, f.name);
  }
  if (folderId) form.append('folder_id', folderId);
  // multipart 의 경우 Content-Type 헤더를 직접 박지 않는다 (boundary 자동).
  const headers = baseGetHeaders(false);
  delete headers['Content-Type'];
  const res = await authFetch(
    `${MATERIAL_PREFIX}/projects/${encodeURIComponent(projectId)}/files`,
    { method: 'POST', body: form, headers },
  );
  return unwrap<CadMaterialUploadResponse>(res);
}

export async function moveCadMaterialFile(
  fileId: string,
  folderId: string | null,
): Promise<void> {
  const res = await authFetch(`${MATERIAL_PREFIX}/files/${encodeURIComponent(fileId)}/move`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ folder_id: folderId }),
  });
  await unwrap<{ moved: boolean }>(res);
}

export async function deleteCadMaterialFile(fileId: string): Promise<void> {
  const res = await authFetch(`${MATERIAL_PREFIX}/files/${encodeURIComponent(fileId)}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  await unwrap<{ deleted: boolean }>(res);
}

export async function fetchCadMaterialFilePreview(
  fileId: string,
): Promise<CadMaterialPreviewResponse> {
  const res = await authFetch(`${MATERIAL_PREFIX}/files/${encodeURIComponent(fileId)}/preview`, {
    headers: jsonHeaders(),
  });
  return unwrap<CadMaterialPreviewResponse>(res);
}

/* ------------------------------------------------------------------ */
/* Messages                                                            */
/* ------------------------------------------------------------------ */

export interface CadMaterialMessagesListOptions {
  /**
   * 지정 시 해당 파일에 묶인 채팅만 반환 (file-scoped 화면). 미지정 시
   * 프로젝트 전반 (legacy 호환).
   */
  fileId?: string | null;
  /**
   * `fileId` 와 함께 쓰는 옵션. true 면 file_id IS NULL 인 legacy 메시지도
   * 함께 노출. 기본 false — 새 화면은 깔끔하게 그 파일의 히스토리만.
   */
  includeLegacyNullFile?: boolean;
}

export async function listCadMaterialMessages(
  projectId: string,
  options: CadMaterialMessagesListOptions = {},
): Promise<CadMaterialMessage[]> {
  const params = new URLSearchParams();
  if (options.fileId) params.set('file_id', options.fileId);
  if (options.includeLegacyNullFile) {
    params.set('include_legacy_null_file', 'true');
  }
  const qs = params.toString();
  const url =
    `${MATERIAL_PREFIX}/projects/${encodeURIComponent(projectId)}/messages`
    + (qs ? `?${qs}` : '');
  const res = await authFetch(url, { headers: jsonHeaders() });
  const data = await unwrap<CadMaterialMessagesResponse>(res);
  return data.messages ?? [];
}

/* ------------------------------------------------------------------ */
/* Chat SSE                                                            */
/* ------------------------------------------------------------------ */

/**
 * 자재관리 in-place revision 동기 payload — backend `_build_file_patched_frame`
 * 가 SSE 끝에 보내는 marker frame 의 ``data.detail`` 모양.
 *
 * 좌측 엑셀 패널이 ``fetchCadMaterialFilePreview`` 캐시를 invalidate 할 때
 * 사용한다. ``revision_index`` 는 캐시 키 갱신용 / 사용자 timeline 표시용.
 */
export interface CadMaterialFilePatchedDetail {
  patch_id: string | null;
  project_id: string;
  file_id: string;
  revision_index: number;
  base_sha256: string | null;
  new_sha256: string;
  size_bytes: number;
  ai_message_id: string | null;
}

export interface CadMaterialChatStreamHandlers {
  /** SSE payload (JSON dict) 가 도착할 때마다 호출. 토큰/완료/에러 모두 전달. */
  onPayload?: (payload: Record<string, unknown>) => void;
  /** 사용자 표시용 텍스트 토큰 (final answer 의 누적). */
  onTextDelta?: (delta: string) => void;
  /** 정상 종료. */
  onCompleted?: (payload: Record<string, unknown>) => void;
  /** 실패. */
  onError?: (error: Error | string) => void;
  /**
   * 자재관리 in-place revision 적용 통지 (backend marker frame).
   *
   * AI 가 선택된 파일을 수정 → backend 가 새 revision 으로 swap 한 직후
   * 호출된다. 좌측 엑셀 미리보기 / 파일 목록을 invalidate 하라는 신호.
   */
  onFilePatched?: (detail: CadMaterialFilePatchedDetail) => void;
}

export interface CadMaterialChatStreamOptions {
  signal?: AbortSignal;
  metadata?: Record<string, unknown>;
  attachments?: unknown[];
  /**
   * 현재 채팅 컨텍스트로 묶일 파일 ID. 지정 시 backend 가:
   *   - file-scoped session (`session_id = "{project_id}:{file_id}"`) 사용,
   *   - 그 파일의 채팅 history 만 AI 에 주입,
   *   - 활성 파일 참조의 첫 자리에 이 파일을 박는다.
   * 미지정 시 프로젝트 전반 (legacy) 흐름.
   */
  fileId?: string | null;
}

/**
 * 자재관리 멀티턴 채팅 SSE.
 *
 * backend `/api/v1/cad/material/projects/{id}/chat/stream` 가 사용자 메시지
 * 영속화 + AI workspace 프록시를 모두 처리하므로 클라이언트는 본 함수 한
 * 번만 호출하면 된다. SSE payload 의 ``data.delta`` / ``data.text`` 토큰을
 * 분해해 ``onTextDelta`` 로 흘려준다.
 */
export async function streamCadMaterialChat(
  projectId: string,
  content: string,
  handlers: CadMaterialChatStreamHandlers,
  options: CadMaterialChatStreamOptions = {},
): Promise<void> {
  const url = `${MATERIAL_PREFIX}/projects/${encodeURIComponent(projectId)}/chat/stream`;
  const body = JSON.stringify({
    content,
    attachments: options.attachments,
    metadata: options.metadata,
    file_id: options.fileId ?? undefined,
  });
  const response = await authFetch(url, {
    method: 'POST',
    headers: { ...jsonHeaders(), Accept: 'text/event-stream' },
    body,
    signal: options.signal,
  });
  if (!response.ok || !response.body) {
    const text = await response.text();
    const err = new Error(`HTTP ${response.status}: ${text}`);
    handlers.onError?.(err);
    throw err;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer = (buffer + decoder.decode(value, { stream: true }))
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');
      let separatorIndex = buffer.indexOf('\n\n');
      while (separatorIndex !== -1) {
        const frame = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);
        separatorIndex = buffer.indexOf('\n\n');
        handleFrame(frame, handlers);
      }
    }
    if (buffer.trim()) {
      handleFrame(buffer, handlers);
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
}

function handleFrame(frame: string, handlers: CadMaterialChatStreamHandlers): void {
  const trimmed = frame.trim();
  if (!trimmed) return;
  if (trimmed.startsWith(':')) return;
  const lines = trimmed.split('\n').filter((l) => l.startsWith('data:'));
  if (lines.length === 0) return;
  const dataText = lines.map((l) => l.slice(5).trimStart()).join('\n').trim();
  if (!dataText || dataText === '[DONE]') return;
  let payload: unknown;
  try {
    payload = JSON.parse(dataText);
  } catch {
    return;
  }
  if (!payload || typeof payload !== 'object') return;
  const obj = payload as Record<string, unknown>;
  handlers.onPayload?.(obj);

  const status = obj['status'];

  // in_progress 토큰 — channel == "answer" + event == "message" + detail: string.
  if (status === 'in_progress') {
    // 자재관리 in-place revision marker frame 우선 처리.
    // backend `_build_file_patched_frame` 가 ``status: 'in_progress'`` 로 깔아
    // 보내므로 기존 ``answer`` 채널 추출보다 먼저 분기한다.
    const patchedDetail = extractFilePatchedDetail(obj);
    if (patchedDetail) {
      handlers.onFilePatched?.(patchedDetail);
      return;
    }
    const delta = extractTokenDelta(obj);
    if (delta) handlers.onTextDelta?.(delta);
    return;
  }

  if (status === 'completed') {
    handlers.onCompleted?.(obj);
    return;
  }

  if (status === 'failed') {
    const message = obj['message'];
    if (typeof message === 'object' && message !== null) {
      const title = (message as Record<string, unknown>)['title'];
      handlers.onError?.(typeof title === 'string' ? title : 'stream failed');
    } else {
      handlers.onError?.('stream failed');
    }
  }
}

/**
 * `AgentInProgressData` (`ai/app/models/agent/response.py`) 의 답변 토큰 추출.
 *
 * 통합 채팅 `chatStore` 의 `isAnswerChannel` + `typeof data.detail === 'string'`
 * 패턴과 동일. 사고/타임라인/위젯 채널은 사용자 답변이 아니므로 무시한다.
 */
function extractTokenDelta(payload: Record<string, unknown>): string | null {
  const data = (payload['data'] && typeof payload['data'] === 'object'
    ? (payload['data'] as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  if (data['channel'] !== 'answer') return null;
  if (data['event'] !== 'message') return null;
  const detail = data['detail'];
  if (typeof detail === 'string' && detail.length > 0) return detail;
  return null;
}

/**
 * 자재관리 in-place revision marker frame 추출.
 *
 * backend `_build_file_patched_frame` 가 보내는 SSE payload:
 *
 *   { status: 'in_progress', data: { event: 'cad_material.file_patched',
 *     channel: 'system', detail: { ... } } }
 *
 * 검사 순서
 * 1. `data.event === 'cad_material.file_patched'` 여야 함.
 * 2. `data.detail` 가 필수 키(file_id, revision_index, new_sha256, size_bytes)
 *    를 모두 갖춘 dict 여야 함. 하나라도 빠지면 null (방어적).
 */
function extractFilePatchedDetail(
  payload: Record<string, unknown>,
): CadMaterialFilePatchedDetail | null {
  const data = (payload['data'] && typeof payload['data'] === 'object'
    ? (payload['data'] as Record<string, unknown>)
    : null);
  if (!data) return null;
  if (data['event'] !== 'cad_material.file_patched') return null;
  const detail = data['detail'];
  if (!detail || typeof detail !== 'object') return null;
  const d = detail as Record<string, unknown>;

  const fileId = d['file_id'];
  const revisionIndex = d['revision_index'];
  const newSha256 = d['new_sha256'];
  const sizeBytes = d['size_bytes'];
  if (
    typeof fileId !== 'string' || !fileId
    || typeof revisionIndex !== 'number'
    || typeof newSha256 !== 'string' || !newSha256
    || typeof sizeBytes !== 'number'
  ) {
    return null;
  }
  return {
    patch_id: typeof d['patch_id'] === 'string' ? (d['patch_id'] as string) : null,
    project_id: typeof d['project_id'] === 'string' ? (d['project_id'] as string) : '',
    file_id: fileId,
    revision_index: revisionIndex,
    base_sha256: typeof d['base_sha256'] === 'string' ? (d['base_sha256'] as string) : null,
    new_sha256: newSha256,
    size_bytes: sizeBytes,
    ai_message_id: typeof d['ai_message_id'] === 'string' ? (d['ai_message_id'] as string) : null,
  };
}


/**
 * `AgentCompletedData.content` 추출 (호출부에서 사용 가능하도록 export).
 *
 * 백엔드 `_extract_final_content` 와 같은 의미. 토큰 누적 결과와 다를 수 있는
 * canonical 최종 텍스트.
 */
export function extractCompletedContent(
  payload: Record<string, unknown>,
): string | null {
  if (payload['status'] !== 'completed') return null;
  const data = (payload['data'] && typeof payload['data'] === 'object'
    ? (payload['data'] as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const content = data['content'];
  if (typeof content === 'string' && content.length > 0) return content;
  return null;
}
